import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker - using local file
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

function App() {
  const [showPdf, setShowPdf] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'embed' | 'direct'>('embed');
  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.15);
  const [showModalGallery, setShowModalGallery] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const modalGalleryRef = useRef<HTMLDivElement>(null);

  // Photo gallery data with captions
  const galleryImages = [
    { src: '/assets/images/1.jpg', caption: 'Querocotillo' },
    { src: '/assets/images/2.jpg', caption: 'Querocotillo' },
    { src: '/assets/images/3.jpg', caption: 'Querocotillo' },
    { src: '/assets/images/4.jpg', caption: 'Querocotillo' },
    { src: '/assets/images/5.jpg', caption: 'Querocotillo' },
    { src: '/assets/images/6.jpg', caption: 'Querocotillo' },
    { src: '/assets/images/7.jpg', caption: 'Querocotillo' },
    { src: '/assets/images/8.png', caption: 'Frontis del Kiosko' },
    { src: '/assets/images/9.jpg', caption: 'El antiguo parque de la ciudad de Querocotillo en el año 1975' },
    { src: '/assets/images/10.jpg', caption: 'La iglesia antigua de Querocotillo en el año 1975' },
    { src: '/assets/images/11.jpg', caption: 'Kiosko del parque antiguo de la ciudad de Querocotillo' },
    { src: '/assets/images/12.jpg', caption: 'Querocotillo' },
    { src: '/assets/images/13.jpg', caption: 'Querocotillo' },
    { src: '/assets/images/14.jpg', caption: 'Querocotillo' },
    { src: '/assets/images/15.jpg', caption: 'Querocotillo' },
    { src: '/assets/images/16.jpg', caption: 'Querocotillo' },
    { src: '/assets/images/17.jpg', caption: 'Querocotillo' },
    { src: '/assets/images/18.jpg', caption: 'Querocotillo' },
    { src: '/assets/images/19.jpg', caption: 'Querocotillo' },
    { src: '/assets/images/20.jpg', caption: 'Querocotillo' },
    { src: '/assets/images/21.jpg', caption: 'Querocotillo' },
  ];

  const displayedImages = showAllPhotos ? galleryImages : galleryImages.slice(0, 8);

  const downloadPdf = () => {
    const link = document.createElement('a');
    link.href = '/assets/research.pdf';
    link.download = 'Investigacion_Querocotillo_Ensor_Vasquez.pdf';
    link.click();
  };

  const openPdfViewer = async () => {
    setShowPdf(true);
    setPdfLoading(true);

    try {
      const loadingTask = pdfjsLib.getDocument('/assets/research.pdf');
      const pdf = await loadingTask.promise;
      setPdfDoc(pdf);
      setTotalPages(pdf.numPages);
      setPdfLoading(false);
    } catch (error) {
      console.error('Error loading PDF:', error);
      setPdfLoading(false);
    }
  };

  const closePdfViewer = () => {
    setShowPdf(false);
    setPdfLoading(false);
    setPdfDoc(null);
    setCurrentPage(1);
  };

  const openPdfDirect = () => {
    window.open('/assets/research.pdf', '_blank');
  };

  const openModalGallery = (index: number) => {
    setCurrentImageIndex(index);
    setShowModalGallery(true);
  };

  const closeModalGallery = () => {
    setShowModalGallery(false);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const showMorePhotos = () => {
    setShowAllPhotos(true);
  };

  // Handle modal click outside to close
  const handleModalClick = (e: React.MouseEvent) => {
    if (modalRef.current && e.target === modalRef.current) {
      closePdfViewer();
    }
  };

  // Handle modal gallery click outside to close
  const handleModalGalleryClick = (e: React.MouseEvent) => {
    if (modalGalleryRef.current && e.target === modalGalleryRef.current) {
      closeModalGallery();
    }
  };

  // Handle escape key to close modals
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showModalGallery) {
          closeModalGallery();
        } else if (showPdf) {
          closePdfViewer();
        }
      }
      if (showModalGallery) {
        if (e.key === 'ArrowRight') {
          nextImage();
        } else if (e.key === 'ArrowLeft') {
          prevImage();
        }
      }
    };

    if (showPdf || showModalGallery) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showPdf, showModalGallery]);

  const renderPage = async (pageNum: number, canvas: HTMLCanvasElement) => {
    if (!pdfDoc) return;

    try {
      const page = await pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale });
      const context = canvas.getContext('2d');

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };

      await page.render(renderContext).promise;
    } catch (error) {
      console.error(`Error rendering page ${pageNum}:`, error);
    }
  };

  const PdfViewer = () => {
    const [renderedPages, setRenderedPages] = useState<Set<number>>(new Set());

    useEffect(() => {
      if (!pdfDoc) return;

      const renderVisiblePages = async () => {
        const container = containerRef.current;
        if (!container) return;

        const canvases = container.querySelectorAll('canvas');
        const containerRect = container.getBoundingClientRect();

        canvases.forEach((canvas, index) => {
          const pageNum = index + 1;
          const canvasRect = canvas.getBoundingClientRect();
          const isVisible = canvasRect.top < containerRect.bottom + 200 &&
            canvasRect.bottom > containerRect.top - 200;

          if (isVisible && !renderedPages.has(pageNum)) {
            renderPage(pageNum, canvas);
            setRenderedPages(prev => new Set([...Array.from(prev), pageNum]));
          }
        });
      };

      renderVisiblePages();

      const handleScroll = () => {
        renderVisiblePages();
      };

      const container = containerRef.current;
      if (container) {
        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
      }
    }, [pdfDoc, renderedPages, scale]);

    if (!pdfDoc) return null;

    return (
      <div className="pdf-canvas-container" ref={containerRef}>
        <div className="pdf-controls">
          <button
            onClick={() => setScale(s => Math.max(0.5, s - 0.25))}
            disabled={scale <= 0.5}
          >
            Zoom -
          </button>
          <span className="zoom-level">{Math.round(scale * 100)}%</span>
          <button
            onClick={() => setScale(s => Math.min(3, s + 0.25))}
            disabled={scale >= 3}
          >
            Zoom +
          </button>
        </div>

        <div className="pdf-pages">
          {Array.from({ length: totalPages }, (_, i) => (
            <div key={i + 1} className="pdf-page">
              <div className="page-number">Página {i + 1}</div>
              <canvas
                className="pdf-canvas"
                style={{
                  width: `${595 * scale}px`,
                  maxWidth: '100%',
                  height: 'auto',
                  display: 'block',
                  margin: '0 auto'
                }}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <h1>Investigación sobre Querocotillo</h1>
          <h2>Por Ensor Vásquez Arenas</h2>
          <p className="subtitle">Cajamarca, Perú</p>
        </div>

        {/* SEO-friendly hidden content for search engines */}
        <div style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
          <h3>Estudio Histórico y Cultural de Querocotillo</h3>
          <p>Investigación académica sobre la historia, tradiciones y patrimonio cultural del pueblo de Querocotillo en la región de Cajamarca, Perú. Trabajo realizado por el economista Ensor Vásquez Arenas, originario de esta comunidad andina.</p>
          <address>
            <strong>Autor:</strong> Ensor Vásquez Arenas<br />
            <strong>Lugar:</strong> Querocotillo, Cajamarca, Perú<br />
            <strong>Temática:</strong> Historia y cultura andina
          </address>
        </div>
      </header>

      <main className="main-content" role="main">
        {/* Desktop side-by-side layout container */}
        <div className="desktop-layout-container">
          {/* Left side - PDF Section */}
          <section className="left-content">
            {/* PDF Section */}
            <article className="pdf-section">
              <div className="pdf-cover-section">
                <h3>Documento de Investigación</h3>
                <div className="pdf-cover-container">
                  <div className="pdf-cover-wrapper" onClick={openPdfViewer}>
                    <img
                      src="/assets/placeholder.png"
                      alt="Portada de la investigación sobre Querocotillo por Ensor Vásquez Arenas"
                      className="pdf-cover-image"
                    />
                    <div className="click-overlay">
                      <div className="click-text">Hacer clic para leer</div>
                    </div>
                  </div>
                  <div className="cover-actions">
                    <button className="view-btn" onClick={openPdfDirect} aria-label="Abrir PDF de la investigación sobre Querocotillo en nueva pestaña">
                      Abrir en nueva pestaña
                    </button>
                    <button className="download-btn" onClick={downloadPdf} aria-label="Descargar PDF de la investigación sobre Querocotillo">
                      Descargar PDF
                    </button>
                  </div>
                </div>
              </div>
            </article>
          </section>

          {/* Right side - Research Info and About Author */}
          <aside className="right-content">
            {/* Research Info Section */}
            <section className="research-intro">
              <h3>Acerca de la Investigación</h3>
              <p>
                La investigación presenta un estudio profundo sobre la caracterización socioeconómica del distrito de <strong>Querocotillo</strong> en el departamento de <strong>Cajamarca, Perú</strong>. La obra documenta datos estadísticos relevantes que han sido recabados de fuentes oficiales del gobierno peruano, mismos que posibilitan tener un diagnóstico actual sobre su historia, principales características, infraestructura, actividades productivas e inversión pública, proporcionando con ello una visión integral de esta importante comunidad cajamarquina que servirá para futuras indagaciones.
              </p>
            </section>

            {/* About Author Section */}
            <section className="about-author">
              <h3>Sobre el Autor</h3>
              <p>
                <strong>Ensor Aníbal Vásquez Arenas</strong>, economista egresado de la <strong>Universidad Nacional de Piura</strong> y originario del distrito de <strong>Querocotillo</strong>, por ende, con el afán de contribuir al desarrollo distrital, se emprendió esta labor investigativa que posibilite una documentación de las principales características que engloban a este distrito.
              </p>
            </section>
          </aside>
        </div>

        {/* Photo Gallery Section - Full Width */}
        <section className="photo-gallery">
          <h3>Anexo Fotográfico Histórico de Querocotillo</h3>
          <div className="gallery-description">
            <p>
              <strong>Sobre el Registro Fotográfico:</strong> Esta colección de imágenes históricas del distrito de Querocotillo está mantenida por <strong>Warren Huffman</strong>, quien se dedica a conservar la memoria visual de esta importante comunidad. La galería incluye fotografías que documentan la evolución del distrito a través del tiempo, honrando la memoria de sus antiguos habitantes al mostrar cómo era su vida y el pueblo que llamaron hogar.
            </p>
          </div>
          <div className="gallery-grid" role="img" aria-label="Galería de fotografías históricas de Querocotillo">
            {displayedImages.map((image, index) => (
              <figure key={index} className="gallery-item" onClick={() => openModalGallery(index)}>
                <img
                  src={image.src}
                  alt={`${image.caption} - Fotografía histórica de Querocotillo, Cajamarca`}
                  className="gallery-image"
                />
                <figcaption className="gallery-caption">
                  {image.caption}
                </figcaption>
              </figure>
            ))}
          </div>

          {!showAllPhotos && galleryImages.length > 8 && (
            <div className="gallery-show-more">
              <button className="show-more-btn" onClick={showMorePhotos} aria-label="Ver más fotografías de Querocotillo">
                Ver más fotos ({galleryImages.length - 8} restantes)
              </button>
            </div>
          )}

          {/* Petition Section */}
          <div className="gallery-petition">
            <div className="petition-content">
              <h4>Ayúdanos a Preservar la Historia</h4>
              <p>
                Si usted posee fotografías antiguas de Querocotillo o información que pueda enriquecer esta galería y la descripción histórica del distrito, por favor contáctenos a <strong><a href="mailto:warren.r.huffman@gmail.com">warren.r.huffman@gmail.com</a></strong>.
              </p>
              <p>
                El propósito de este proyecto es preservar la historia de Querocotillo para las generaciones venideras, y necesitamos actuar ahora mientras esta parte de nuestra historia aún permanece en la memoria viva de nuestros habitantes.
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* PDF Modal */}
      {showPdf && (
        <div className="pdf-modal-overlay" ref={modalRef} onClick={handleModalClick}>
          <div className="pdf-modal">
            <div className="pdf-modal-header">
              <h3>Visor del Documento</h3>
              <div className="modal-controls">
                <button
                  className={`view-mode-btn ${viewMode === 'embed' ? 'active' : ''}`}
                  onClick={() => setViewMode('embed')}
                >
                  Vista Optimizada
                </button>
                <button
                  className={`view-mode-btn ${viewMode === 'direct' ? 'active' : ''}`}
                  onClick={() => setViewMode('direct')}
                >
                  Vista Directa
                </button>
                <button className="close-modal" onClick={closePdfViewer}>
                  ✕ Cerrar
                </button>
              </div>
            </div>

            <div className="pdf-modal-content">
              {pdfLoading && (
                <div className="pdf-loading">
                  <div className="loading-spinner"></div>
                  <p>Cargando documento...</p>
                </div>
              )}

              {!pdfLoading && viewMode === 'embed' ? (
                <PdfViewer />
              ) : !pdfLoading && viewMode === 'direct' ? (
                <div className="pdf-direct-view">
                  <div className="direct-view-content">
                    <h4>Vista Directa del Documento</h4>
                    <p>Para una mejor experiencia de lectura, haga clic en el botón de abajo para abrir el PDF en una nueva pestaña.</p>
                    <button className="open-direct-btn" onClick={openPdfDirect}>
                      🔗 Abrir PDF en Nueva Pestaña
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Modal Gallery */}
      {showModalGallery && (
        <div className="modal-gallery-overlay" ref={modalGalleryRef} onClick={handleModalGalleryClick}>
          <div className="modal-gallery">
            <div className="modal-gallery-header">
              <h3>Galería Fotográfica</h3>
              <button className="close-modal" onClick={closeModalGallery}>
                ✕ Cerrar
              </button>
            </div>

            <div className="modal-gallery-content">
              <button className="gallery-nav-btn prev-btn" onClick={prevImage}>
                ‹
              </button>

              <div className="modal-image-container">
                <img
                  src={galleryImages[currentImageIndex].src}
                  alt={galleryImages[currentImageIndex].caption}
                  className="modal-gallery-image"
                />
                <div className="modal-image-info">
                  <p className="modal-image-caption">{galleryImages[currentImageIndex].caption}</p>
                  <p className="modal-image-counter">
                    {currentImageIndex + 1} de {galleryImages.length}
                  </p>
                </div>
              </div>

              <button className="gallery-nav-btn next-btn" onClick={nextImage}>
                ›
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className="App-footer">
        <div className="footer-content">
          <div className="footer-text">
            <p>© 2025 - Investigación sobre Querocotillo, Cajamarca</p>
            <p>Trabajo de investigación por Ensor Vásquez Arenas</p>
          </div>
          <div className="footer-logo">
            <img src="/assets/logo.png" alt="Logo" />
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
